import { useState } from "react";

export interface DecisionNode {
  id: string;
  prompt: string;
  context?: string;
  options: {
    label: string;
    outcome: string;
    correct: boolean;
    next?: string;
  }[];
}

interface DecisionTreeProps {
  title: string;
  description: string;
  nodes: DecisionNode[];
  onComplete?: (score: number, passed: boolean) => void;
  onClose?: () => void;
}

export function DecisionTree({ title, description, nodes, onComplete, onClose }: DecisionTreeProps) {
  const [currentNodeId, setCurrentNodeId] = useState(nodes[0]?.id);
  const [history, setHistory] = useState<{ nodeId: string; optionIdx: number; correct: boolean }[]>([]);
  const [showOutcome, setShowOutcome] = useState(false);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [finished, setFinished] = useState(false);

  const currentNode = nodes.find((n) => n.id === currentNodeId);
  const totalDecisions = history.length;
  const correctDecisions = history.filter((h) => h.correct).length;
  const score = totalDecisions > 0 ? Math.round((correctDecisions / totalDecisions) * 100) : 0;

  function handleSelect(idx: number) {
    if (!currentNode) return;
    setSelectedOption(idx);
    setShowOutcome(true);
    const option = currentNode.options[idx];
    const isCorrect = option.correct;
    setHistory((prev) => [...prev, { nodeId: currentNode.id, optionIdx: idx, correct: isCorrect }]);

    if (!option.next || !nodes.find((n) => n.id === option.next)) {
      setFinished(true);
      const finalScore = Math.round(((correctDecisions + (isCorrect ? 1 : 0)) / (totalDecisions + 1)) * 100);
      onComplete?.(finalScore, finalScore >= 70);
    }
  }

  function handleContinue() {
    if (!currentNode || selectedOption === null) return;
    const option = currentNode.options[selectedOption];
    if (option.next && nodes.find((n) => n.id === option.next)) {
      setCurrentNodeId(option.next);
      setSelectedOption(null);
      setShowOutcome(false);
    }
  }

  if (finished) {
    return (
      <div className="card" style={{ padding: "var(--space-5)", maxWidth: "600px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "var(--space-4)" }}>
          <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>{score >= 70 ? "\u2705" : "\u26A0"}</div>
          <h3 style={{ fontSize: "1.25rem", marginBottom: "0.25rem" }}>{score >= 70 ? "Exercise Complete!" : "Review Required"}</h3>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: score >= 70 ? "var(--accent-600)" : "var(--warning-600)" }}>
            {score}%
          </div>
          <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
            {correctDecisions} of {totalDecisions} correct decisions
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "var(--space-4)" }}>
          {history.map((h, i) => {
            const node = nodes.find((n) => n.id === h.nodeId);
            const opt = node?.options[h.optionIdx];
            return (
              <div key={i} style={{ padding: "0.5rem 0.75rem", borderRadius: "var(--radius-sm)", fontSize: "0.75rem", background: h.correct ? "var(--accent-50)" : "var(--error-50)", border: `1px solid ${h.correct ? "var(--accent-300)" : "var(--error-300)"}` }}>
                <span style={{ fontWeight: 600 }}>{h.correct ? "\u2713" : "\u2717"}</span> {opt?.label}
              </div>
            );
          })}
        </div>
        <button onClick={onClose} className="btn btn-primary" style={{ width: "100%" }}>Continue</button>
      </div>
    );
  }

  if (!currentNode) return null;

  return (
    <div className="card" style={{ padding: "var(--space-5)", maxWidth: "600px", margin: "0 auto" }}>
      <div style={{ marginBottom: "var(--space-3)" }}>
        <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--accent-600)", marginBottom: "0.25rem" }}>
          Decision {totalDecisions + 1}
        </div>
        <h3 style={{ fontSize: "1.125rem", marginBottom: "0.25rem" }}>{title}</h3>
        <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>{description}</p>
      </div>

      {currentNode.context && (
        <div style={{ padding: "0.75rem", borderRadius: "var(--radius-md)", background: "var(--bg-subtle)", marginBottom: "var(--space-3)", fontSize: "0.8125rem", fontStyle: "italic" }}>
          {currentNode.context}
        </div>
      )}

      <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: "var(--space-3)" }}>{currentNode.prompt}</div>

      {!showOutcome ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {currentNode.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              style={{
                padding: "0.75rem 1rem",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border)",
                background: "var(--surface)",
                cursor: "pointer",
                textAlign: "left",
                fontSize: "0.875rem",
                transition: "all var(--transition-fast)",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : (
        <div>
          <div style={{ padding: "0.75rem", borderRadius: "var(--radius-md)", marginBottom: "0.75rem", border: `1px solid ${selectedOption !== null && currentNode.options[selectedOption].correct ? "var(--accent-300)" : "var(--error-300)"}`, background: selectedOption !== null && currentNode.options[selectedOption].correct ? "var(--accent-50)" : "var(--error-50)" }}>
            <div style={{ fontWeight: 600, fontSize: "0.8125rem", marginBottom: "0.25rem", color: selectedOption !== null && currentNode.options[selectedOption].correct ? "var(--accent-700)" : "var(--error-700)" }}>
              {selectedOption !== null && currentNode.options[selectedOption].correct ? "Correct Decision" : "Incorrect Decision"}
            </div>
            <p style={{ fontSize: "0.8125rem", color: "var(--text)" }}>
              {selectedOption !== null && currentNode.options[selectedOption].outcome}
            </p>
          </div>
          <button onClick={handleContinue} className="btn btn-primary" style={{ width: "100%" }}>
            Continue
          </button>
        </div>
      )}
    </div>
  );
}
